import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { pageConfigs } from 'modules/account/configs/pageConfigs';

import { ToogleWhatsappResend } from 'utils/toggleWhatsappResend';

import { Form } from 'components/ds';
import { FooterDiv } from '../../../../components/Css';

import { ResendButton } from '../../pages/validation/styles';

import {
  CodeItem,
  Container,
  ItemInputContainer,
  ItemWrapper,
  SendTextData,
} from './TwoFactorValidationCodeComponent.styles';

const DEFAULT_TIME = 180;

interface TwoFactorValidationCodeProps {
  code: string;
  isResendDisabled: boolean;
  channelValue?: string;
  onCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onResend: () => void;
  onSubmit: () => void;
  setIsResendDisabled: React.Dispatch<boolean>;
}

export const TwoFactorValidationCodeComponent = ({
  code,
  isResendDisabled,
  channelValue,
  onCodeChange,
  onSubmit,
  setIsResendDisabled,
}: TwoFactorValidationCodeProps) => {
  // Hooks
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // States
  const [seconds, setSeconds] = useState<number>(DEFAULT_TIME);

  // Methods
  const handleBack = () => {
    navigate(pageConfigs.validateOtpResetPassword.navigateBack());
  };

  const handleInputChange = (index: number, value: string) => {
    if (code.length === 6 && code[index]) {
      return;
    }

    const newCode = code.split('');
    newCode[index] = value;

    const event = {
      target: { value: newCode.join('') },
    } as React.ChangeEvent<HTMLInputElement>;

    onCodeChange(event);

    if (value && index < 5) {
      const nextInput = document.querySelector(
        `input[data-index="${index + 1}"]`,
      );
      (nextInput as HTMLElement)?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace') {
      if (code[index]) {
        const newCode = code.split('');
        newCode[index] = '';

        const event = {
          target: { value: newCode.join('') },
        } as React.ChangeEvent<HTMLInputElement>;

        onCodeChange(event);
      } else if (index > 0) {
        const prevInput = document.querySelector(
          `input[data-index="${index - 1}"]`,
        );
        (prevInput as HTMLElement)?.focus();
      }

      e.preventDefault();
    }
  };

  const handleOnResentCode = async () => {
    try {
      await ToogleWhatsappResend(true);

      setSeconds(DEFAULT_TIME);
      setIsResendDisabled(true);

      handleBack();
    } catch (error) {
      console.error('Erro ao reenviar código:', error);
    }
  };

  // Effects
  useEffect(() => {
    if (seconds > 0) {
      const timer = setTimeout(() => setSeconds(seconds - 1), 1000);

      return () => clearTimeout(timer);
    }

    setIsResendDisabled(false);
  }, [seconds]);

  // Renders
  return (
    <Container>
      <ItemWrapper>
        <SendTextData>
          {(() => {
            try {
              const theme = JSON.parse(localStorage.getItem('theme') || '{}');
              const isSimpleMessage = theme?.otp?.simpleMessage;
              
              if (isSimpleMessage && channelValue) {
                return `Digite o código enviado para ${channelValue}`;
              }
              
              return channelValue ?
                `Digite o código enviado para ${channelValue} para definir sua senha`
                : "Digite o código enviado para definir sua senha";
            } catch {
              return channelValue ?
                `Digite o código enviado para ${channelValue} para definir sua senha`
                : "Digite o código enviado para definir sua senha";
            }
          })()}
        </SendTextData>
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <ItemInputContainer>
            {[...Array(6)].map((_, index) => (
              <CodeItem
                key={index}
                data-index={index}
                variant="square"
                type="number"
                maxLength={1}
                value={code[index] || ''}
                onChange={(e) =>
                  handleInputChange(index, e.target.value.slice(-1))
                }
                onKeyDown={(e) => handleKeyDown(index, e)}
                autoFocus={index === 0}
              />
            ))}
          </ItemInputContainer>
        </Form>
      </ItemWrapper>
      
      <FooterDiv style={{marginBottom: '16px'}}>
        <ResendButton
          className="pwa-button-outline"
          type="default"
          onClick={handleOnResentCode}
          disabled={isResendDisabled}
        >
          {seconds <= 0
            ? 'Reenviar agora'
            : seconds >= 60
              ? `Reenviar em ${Math.floor(seconds / 60)}min e ${seconds % 60}s`
              : `Reenviar em ${seconds} segundos`}
        </ResendButton>
      </FooterDiv>
    </Container>
  );
};
